import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { SortOption } from '../../Sell';
import { ItemDTO } from '../../types/Inventory';
import api from '../../../../../api/axios';
import {InventoryFilterState} from '../../../../skin-db/database/filter/SkinDbFilter';

// Define the missing interfaces based on your component logic
interface InventoryResponse {
  more?: boolean;
  more_start?: string;
  items: any[];
}

interface FetchInventoryResponse {
  inventory: InventoryResponse;
}

export const DEFAULTS = {
  minPrice: 0,
  maxPrice: 100000,
  floatMin: 0,
  floatMax: 1,
  minFade: 80,
  maxFade: 100,
  minBlue: 0,
  maxBlue: 100,
  paintSeed: null as number | null,
};

export const useInventory = (
    steamAccountId: string | undefined,
    steamAccountSteamId: string | undefined,
    filters: InventoryFilterState,
    sortBy: SortOption,
    searchTerm: string
) => {
  const [items, setItems] = useState<ItemDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualCooldown, setManualCooldown] = useState(0);
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);

  // Check extension status
  useEffect(() => {
    const checkExt = () => {
      // @ts-ignore
      const installed = globalThis.MOONSKINS_EXTENSION_ENABLED === true;
      setIsExtensionInstalled(installed);
    };
    checkExt();
    window.addEventListener('MOONSKINS_EXTENSION_INSTALLED', checkExt);
    return () => window.removeEventListener('MOONSKINS_EXTENSION_INSTALLED', checkExt);
  }, []);

  // Cooldown timer logic
  useEffect(() => {
    const checkCooldown = () => {
      const lastFetch = localStorage.getItem('inventory_last_fetch');
      const lastMethod = localStorage.getItem('inventory_last_method');
      if (!lastFetch) {
        setManualCooldown(0);
        return;
      }
      const now = Date.now();
      const diff = Math.floor((now - parseInt(lastFetch)) / 1000);
      const limit = lastMethod === 'extension' ? 3 : 10;
      if (diff < limit) {
        setManualCooldown(limit - diff);
      } else {
        setManualCooldown(0);
      }
    };
    checkCooldown();
    const interval = setInterval(checkCooldown, 1000);
    return () => clearInterval(interval);
  }, [isExtensionInstalled]);

  const buildParams = () => {
    const params: Record<string, any> = {};
    if (searchTerm.trim().length > 0) params.name = searchTerm;
    const arrayKeys: (keyof InventoryFilterState)[] = [
      'exterior', 'extra', 'type', 'pistols', 'smg', 'rifles', 'snipers',
      'shotguns', 'machineguns', 'knives', 'equipment', 'unique', 'phase',
      'rarity', 'rarePattern',
    ];
    arrayKeys.forEach((key) => {
      const value = filters[key];
      if (Array.isArray(value) && value.length > 0) params[key] = value;
    });
    (Object.entries(DEFAULTS) as [keyof typeof DEFAULTS, any][]).forEach(([key, def]) => {
      const current = filters[key as keyof InventoryFilterState];
      if (key !== 'paintSeed' && current !== def) {
        params[key] = current;
      } else if (key === 'paintSeed' && current !== null && String(current) !== '') {
        params[key] = current;
      }
    });
    params.sort = sortBy;
    return params;
  };

  const loadInventory = async (isManual: boolean = false) => {
    if (!steamAccountId) {
      setItems([]);
      return;
    }
    if (isManual && manualCooldown > 0) return;

    setLoading(true);
    setError(null);

    const fetchViaApi = async (method: 'api' | 'extension', extensionPayload?: InventoryResponse) => {
      const params = buildParams();

      // If we got data from the extension, sync it with the backend first
      if (method === 'extension' && extensionPayload) {
        await api.post(`/steam-account/${steamAccountId}/sync-inventory`, {
          inventory: extensionPayload
        });
      }

      // Fetch the filtered inventory from our backend
      const res = await api.get(`/steam-account/${steamAccountId}/inventory`, { params });
      const apiItems: ItemDTO[] = res.data;
      const normalized = apiItems.map((item) => ({
        ...item,
        image: item.catalog?.imageLink || undefined,
      }));
      setItems(normalized);

      const currentLastFetch = localStorage.getItem('inventory_last_fetch');
      if (isManual || !currentLastFetch) {
        localStorage.setItem('inventory_last_fetch', Date.now().toString());
        localStorage.setItem('inventory_last_method', method);
      }
      if (isManual) {
        toast.success('Inventory updated');
      }
    };

    try {
      if (isExtensionInstalled && manualCooldown === 0) {
        const requestId = Math.floor(Math.random() * 1000000);
        const extensionPromise = new Promise<void>((resolve, reject) => {
          const handleResponse = (event: MessageEvent) => {
            if (event.data?.target === 'MOONSKINS_PAGE' && event.data?.id === requestId) {
              window.removeEventListener('message', handleResponse);
              if (event.data?.success) {
                const payload = event.data.payload as FetchInventoryResponse;
                fetchViaApi('extension', payload.inventory).then(resolve).catch(reject);
              } else {
                reject(new Error(event.data?.error || 'Extension error'));
              }
            }
          };

          // Extension timeout handling
          setTimeout(() => {
            window.removeEventListener('message', handleResponse);
            reject(new Error('Extension timeout'));
          }, 8000);

          window.addEventListener('message', handleResponse);
          window.postMessage({
            target: 'MOONSKINS_EXTENSION',
            version: 1,
            request_type: 15,
            payload: { steamAccountId: steamAccountId, steamAccountSteamId: steamAccountSteamId, appId: 730, contextId: 2 },
            id: requestId
          }, '*');
        });
        await extensionPromise;
      } else {
        await fetchViaApi('api');
      }
    } catch (extErr: any) {
      console.warn('Extension fetch failed, falling back to API', extErr);
      await fetchViaApi('api');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInventory();
  }, [steamAccountId, JSON.stringify(filters), sortBy, searchTerm]);

  const removeItemsFromState = (assetIdsToRemove: string[]) => {
    setItems((prevItems) => prevItems.filter(item => !assetIdsToRemove.includes(item.itemAssetId)));
  };

  return { items, loading, error, manualCooldown, isExtensionInstalled, loadInventory, removeItemsFromState };
};